/**
 * Theme Configuration System
 *
 * Provides functions to get and validate theme configurations.
 * The active theme is determined by the VUE_APP_THEME environment variable.
 */

import { westmorelandTheme } from './westmoreland'
import { westmoreland2Theme } from './westmoreland2'
import { windermereTheme } from './windermere'
import { windermere2Theme } from './windermere2'
import svdemoTheme from './svdemo'

/**
 * Registry of all available themes
 *
 * V2 themes use the Soft Modern design system with:
 * - Layered shadows instead of borders
 * - Gradient buttons with glow effects
 * - Glass morphism overlays
 */
const themes = {
  westmoreland: westmorelandTheme,
  westmoreland2: westmoreland2Theme,
  windermere: windermereTheme,
  windermere2: windermere2Theme,
  svdemo: svdemoTheme,
}

/**
 * Default theme when none is specified or invalid
 */
const DEFAULT_THEME = 'westmoreland'

/**
 * Theme aliases for user-friendly URL parameters
 * Maps user-friendly names to internal theme names
 */
const themeAliases = {
  'westmoreland_v2': 'westmoreland2',
  'westmoreland-v2': 'westmoreland2',
  'windermere_v2': 'windermere2',
  'windermere-v2': 'windermere2'
}

/**
 * Resolve theme name, checking aliases
 */
function resolveThemeName(themeName) {
  if (!themeName) return null
  // Check aliases first
  if (themeAliases[themeName]) {
    return themeAliases[themeName]
  }
  // Return as-is if valid
  return themeName
}

/**
 * Get the currently active theme name
 * Priority: URL parameter (window.VUE_APP_THEME) > env var > default
 */
export function getActiveTheme() {
  // First check window variable (set by URL parameter in index.html)
  if (typeof window !== 'undefined' && window.VUE_APP_THEME) {
    const windowTheme = resolveThemeName(window.VUE_APP_THEME)
    if (windowTheme && isValidTheme(windowTheme)) {
      return windowTheme
    }
  }

  // Then check environment variable
  const envTheme = process.env.VUE_APP_THEME
  if (envTheme && isValidTheme(envTheme)) {
    return envTheme
  }

  return DEFAULT_THEME
}

/**
 * Check if a theme name is valid
 */
export function isValidTheme(themeName) {
  if (!themeName) return false
  return themeName in themes
}

/**
 * Get the configuration for the active theme
 */
export function getThemeConfig() {
  const themeName = getActiveTheme()
  return themes[themeName]
}

/**
 * Get the configuration for a specific theme by name
 */
export function getThemeConfigByName(themeName) {
  if (!isValidTheme(themeName)) {
    throw new Error(`Invalid theme name: ${themeName}`)
  }
  return themes[themeName]
}

/**
 * Get all available theme names
 */
export function getAvailableThemes() {
  return Object.keys(themes)
}

/**
 * Export individual themes for direct access
 */
export { westmorelandTheme, westmoreland2Theme, windermereTheme, windermere2Theme }
